// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useRippleDelegated } from "@/hooks/use-ripple";
import { cn } from "@/lib/utils";

const ITEM_HEIGHT = 36;

interface FilterSelectOption {
  label: string;
  value: string;
  /** Draws a small dot before the label. `markTitle` says what it means. */
  marked?: boolean;
}

const PILL_STYLE =
  "bg-green-100 text-green-900 border border-green-500/60 dark:bg-green-950 dark:text-green-300 dark:border-green-700";

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: FilterSelectOption[];
  searchPlaceholder: string;
  emptyText: string;
  loading?: boolean;
  /** Hide the X (clear) button next to the selected value */
  hideClear?: boolean;
  /** Block the trigger while a change would be unsafe (e.g. unsaved edits on the current scope) */
  disabled?: boolean;
  /** Accessible name for the dot drawn against `marked` options. */
  markTitle?: string;
}

export function FilterSelect({
  label,
  value,
  onChange,
  options,
  searchPlaceholder,
  emptyText,
  loading,
  hideClear,
  disabled,
  markTitle,
}: FilterSelectProps) {
  const { t } = useTranslation("back-office");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  useRippleDelegated(scrollEl);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const base = q === "" ? options : options.filter((o) => o.label.toLowerCase().includes(q));
    if (q === "" && value) {
      const selectedIdx = base.findIndex((o) => o.value === value);
      if (selectedIdx > 0) {
        return [base[selectedIdx], ...base.slice(0, selectedIdx), ...base.slice(selectedIdx + 1)];
      }
    }
    return base;
  }, [options, search, value]);

  useEffect(function resetFocusIndexOnMount() {
    setFocusedIndex(-1);
  }, []);

  useEffect(
    function scrollToTopOnOpen() {
      if (open && scrollEl) scrollEl.scrollTop = 0;
    },
    [open, scrollEl],
  );

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 8,
  });

  function select(option: FilterSelectOption) {
    onChange(option.value);
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(focusedIndex + 1, filtered.length - 1);
      setFocusedIndex(next);
      virtualizer.scrollToIndex(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = Math.max(focusedIndex - 1, 0);
      setFocusedIndex(prev);
      virtualizer.scrollToIndex(prev);
    } else if (e.key === "Enter" && focusedIndex >= 0) {
      e.preventDefault();
      const item = filtered[focusedIndex];
      if (item) select(item);
    }
  }

  const selectedLabel = options.find((o) => o.value === value)?.label;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTimeout(() => setSearch(""), 150);
        if (o) setFocusedIndex(-1);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={loading || disabled}
          className={`font-normal gap-1.5 ${value ? "bg-sidebar-accent-hover hover:bg-sidebar-accent border-sidebar-accent hover:border-sidebar-primary text-sidebar-accent-foreground hover:text-sidebar-accent-foreground dark:bg-sidebar-accent dark:border-sidebar-accent dark:hover:bg-sidebar-accent-hover dark:hover:border-sidebar-primary" : "border-dashed"}`}
        >
          <span className={loading ? "text-muted-foreground" : ""}>{loading ? t("common.loading") : label}</span>
          {value && selectedLabel && (
            <>
              <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-sm font-medium ${PILL_STYLE}`}>
                {selectedLabel}
              </span>
              {!hideClear && (
                // Rendered as a span, not a <button>: the PopoverTrigger renders this
                // Button as a <button>, and a nested <button> is invalid HTML.
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={t("common.clear")}
                  className="ml-0.5 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onChange("");
                    }
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <X className="h-3 w-3" />
                </span>
              )}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start" side="bottom" sideOffset={-36}>
        <div className="flex items-center gap-2 px-3 py-2 border-b">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        <div
          ref={setScrollEl}
          className="overflow-y-auto rounded-b-md"
          style={{ height: Math.min(filtered.length * ITEM_HEIGHT, 220) }}
          onWheel={(e) => e.stopPropagation()}
        >
          {filtered.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualizer.getVirtualItems().map((vItem) => {
                const option = filtered[vItem.index];
                if (!option) return null;
                const isSelected = option.value === value;
                const isFocused = vItem.index === focusedIndex;
                const isPinnedSelected = isSelected && vItem.index === 0 && !search;
                return (
                  <div
                    key={option.value}
                    data-ripple
                    style={{ position: "absolute", top: vItem.start, left: 0, right: 0, height: ITEM_HEIGHT }}
                    className={cn(
                      "relative overflow-hidden flex items-center gap-2 px-2 cursor-pointer text-sm hover:bg-accent",
                      isFocused ? "bg-accent" : isSelected ? "bg-accent/60" : "",
                      isPinnedSelected && "border-b",
                    )}
                    onClick={() => select(option)}
                  >
                    {option.marked && (
                      // `role="img"` so the dot carries its meaning to a screen reader; a bare
                      // span has no role that supports aria-label.
                      <span
                        role="img"
                        title={markTitle}
                        aria-label={markTitle}
                        className="size-1.5 shrink-0 rounded-full bg-primary"
                      />
                    )}
                    <span className="flex-1 truncate">{option.label}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
